import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Pencil, Car, MapPin, Euro, Calendar, Users, DoorOpen,
  Fuel, Settings2, Layers, Tag, Sparkles, PlusCircle, Navigation,
  Eye, CheckCircle2, XCircle, Clock, FileText, Upload, Trash2,
  Wrench, Save, ExternalLink, Plus, IdCard, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateDE, formatDateTimeDE } from "@/lib/utils";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
function resolveImg(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return BACKEND + url;
  return url;
}

const STATUS = {
  pending: { label: "Ausstehend", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Bestätigt", cls: "bg-emerald-100 text-emerald-800" },
  active: { label: "Aktiv", cls: "bg-blue-100 text-blue-800" },
  completed: { label: "Abgeschlossen", cls: "bg-[#F4F4F4] text-[#262626]" },
  cancelled: { label: "Storniert", cls: "bg-red-100 text-red-800" },
};

const TABS = [
  { key: "uebersicht", label: "Übersicht" },
  { key: "daten", label: "Fahrzeugdaten" },
  { key: "wartung", label: "Wartung" },
];

export default function AdminVehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("uebersicht");
  const [vehicle, setVehicle] = useState(null);
  const [category, setCategory] = useState(null);
  const [group, setGroup] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [v, cats, grps, allBookings] = await Promise.all([
        api.get(`/vehicles/${id}`),
        api.get("/admin/vehicle-categories"),
        api.get("/admin/vehicle-groups"),
        api.get("/admin/bookings"),
      ]);
      setVehicle(v.data);
      setCategory(cats.data.find((c) => c.id === v.data.vehicle_category_id) || null);
      setGroup(grps.data.find((g) => g.id === v.data.vehicle_group_id) || null);
      setBookings(allBookings.data.filter((b) => b.vehicle_id === id));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const stats = useMemo(() => {
    const paid = bookings.filter((b) => b.status !== "cancelled");
    return {
      total: bookings.length,
      revenue: paid.reduce((s, b) => s + (b.total || 0), 0),
      active: bookings.filter((b) => b.status === "active" || b.status === "confirmed").length,
      completed: bookings.filter((b) => b.status === "completed").length,
      cancelled: bookings.filter((b) => b.status === "cancelled").length,
    };
  }, [bookings]);

  const goEdit = () => navigate("/admin/fahrzeuge", { state: { editVehicleId: id } });

  if (loading) return <div className="text-[#525252]">Lädt...</div>;
  if (!vehicle) return <div className="text-[#525252]">Fahrzeug nicht gefunden.</div>;

  const images = vehicle.images && vehicle.images.length > 0
    ? vehicle.images
    : (vehicle.image_url ? [vehicle.image_url] : []);
  const pos = vehicle.last_position;

  return (
    <div className="rf-fade-in" data-testid="admin-vehicle-detail">
      <Link to="/admin/fahrzeuge" className="inline-flex items-center gap-1.5 text-sm text-[#525252] hover:text-[#E11226] mb-5" data-testid="back-to-vehicles">
        <ArrowLeft size={14} /> Zurück zur Fahrzeugliste
      </Link>

      <div className="flex items-center gap-5 mb-8 flex-wrap">
        <img
          src={resolveImg(images[0])}
          alt=""
          className="w-24 h-20 object-cover rounded-lg border border-[#E5E5E5] shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="text-xs tracking-[0.2em] uppercase text-[#E11226] font-semibold">Fahrzeugprofil</div>
          <h1 className="font-display text-3xl font-bold text-[#0A0A0A] mt-1 truncate">
            {vehicle.brand} {vehicle.name}
          </h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <Badge className={vehicle.active ? "bg-emerald-100 text-emerald-800 border-0" : "bg-red-100 text-red-800 border-0"}>
              {vehicle.active ? "Aktiv" : "Inaktiv"}
            </Badge>
            <span className="text-xs text-[#525252] font-mono">ID: {vehicle.id.slice(0, 8).toUpperCase()}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/tracking">
            <Button variant="outline" data-testid="vehicle-tracking-btn">
              <Navigation size={14} className="mr-2" /> GPS-Tracking
            </Button>
          </Link>
          <Button onClick={goEdit} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="vehicle-edit-btn">
            <Pencil size={14} className="mr-2" /> Bearbeiten
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E5E5] mb-8">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-[#E11226] text-[#0A0A0A]"
                : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
            }`}
            data-testid={`vehicle-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "uebersicht" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Stat icon={Calendar} label="Buchungen" value={stats.total} testid="veh-stat-bookings" />
            <Stat icon={Euro} label="Umsatz" value={`${stats.revenue.toFixed(0)}€`} accent testid="veh-stat-revenue" />
            <Stat icon={Clock} label="Aktiv" value={stats.active} testid="veh-stat-active" />
            <Stat icon={CheckCircle2} label="Abgeschlossen" value={stats.completed} testid="veh-stat-completed" />
            <Stat icon={XCircle} label="Storniert" value={stats.cancelled} testid="veh-stat-cancelled" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-6">
              <div className="bg-white border border-[#E5E5E5] rounded-lg p-6">
                <h3 className="font-display font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                  <Car size={16} /> Fahrzeugdaten
                </h3>
                <div className="space-y-3 text-sm">
                  <InfoRow icon={Tag} label="Kategorie" value={vehicle.category} />
                  {category && <InfoRow icon={Layers} label="Eigene Kategorie" value={category.name} />}
                  {group && <InfoRow icon={Layers} label="Gruppe" value={group.name} />}
                  {group?.min_age && <InfoRow icon={Users} label="Mindestalter" value={`${group.min_age} Jahre`} />}
                  {group && <InfoRow icon={Euro} label="Kaution" value={`${Number(group.deposit_amount || 0).toFixed(2)}€`} />}
                  <InfoRow icon={Settings2} label="Getriebe" value={vehicle.transmission} />
                  <InfoRow icon={Fuel} label="Kraftstoff" value={vehicle.fuel} />
                  <InfoRow icon={Users} label="Sitzplätze" value={vehicle.seats} />
                  <InfoRow icon={DoorOpen} label="Türen" value={vehicle.doors} />
                  <InfoRow icon={Euro} label="Preis/Tag" value={`${vehicle.base_price_per_day ?? vehicle.price_per_day}€`} />
                  {vehicle.special && (
                    <InfoRow icon={Sparkles} label={`Sonderpreis (${vehicle.special.name})`} value={`${vehicle.price_per_day}€`} />
                  )}
                  <InfoRow icon={MapPin} label="Standort" value={vehicle.location_name || "—"} />
                  <InfoRow icon={Calendar} label="Angelegt" value={formatDateDE(vehicle.created_at)} />
                </div>
              </div>

              {pos && (
                <div className="bg-white border border-[#E5E5E5] rounded-lg p-6" data-testid="vehicle-gps-card">
                  <h3 className="font-display font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                    <Navigation size={16} /> Letzte GPS-Position
                  </h3>
                  <div className="space-y-3 text-sm">
                    <InfoRow icon={MapPin} label="Koordinaten" value={`${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`} />
                    <InfoRow icon={Settings2} label="Status" value={pos.status} />
                    <InfoRow icon={Clock} label="Zeitstempel" value={formatDateTimeDE(pos.ts)} />
                  </div>
                </div>
              )}
            </aside>

            {/* Main */}
            <section className="lg:col-span-8 space-y-6">
              {images.length > 0 && (
                <div className="bg-white border border-[#E5E5E5] rounded-lg p-6">
                  <h3 className="font-display font-semibold text-[#0A0A0A] mb-4">Bildergalerie</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {images.map((url, i) => (
                      <img
                        key={url + i}
                        src={resolveImg(url)}
                        alt=""
                        className={`w-full h-28 object-cover rounded-md border ${i === 0 ? "border-[#E11226]" : "border-[#E5E5E5]"}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {vehicle.description && (
                <div className="bg-white border border-[#E5E5E5] rounded-lg p-6">
                  <h3 className="font-display font-semibold text-[#0A0A0A] mb-3">Beschreibung</h3>
                  <p className="text-sm text-[#262626] leading-relaxed whitespace-pre-line">{vehicle.description}</p>
                </div>
              )}

              <div className="bg-white border border-[#E5E5E5] rounded-lg p-6" data-testid="vehicle-features-card">
                <h3 className="font-display font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2">
                  <Sparkles size={16} /> Ausstattung ({vehicle.features?.length || 0})
                </h3>
                {vehicle.features?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {vehicle.features.map((f) => (
                      <span key={f} className="text-xs px-2.5 py-1 rounded-sm border border-[#E5E5E5] bg-[#FAFAFA] text-[#262626]">{f}</span>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#525252]">Keine Ausstattung hinterlegt.</p>}
              </div>

              <div className="bg-white border border-[#E5E5E5] rounded-lg p-6" data-testid="vehicle-extras-card">
                <h3 className="font-display font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2">
                  <PlusCircle size={16} /> Zusatzleistungen ({vehicle.extras?.length || 0})
                </h3>
                {vehicle.extras?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {vehicle.extras.map((e) => (
                      <span key={e.id} className="text-xs px-2.5 py-1 rounded-sm border border-[#E5E5E5] bg-[#FAFAFA] text-[#262626]">
                        {e.name} · {Number(e.price).toFixed(2)}€/Tag
                      </span>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#525252]">Für dieses Fahrzeug sind keine Zusatzleistungen freigegeben.</p>}
              </div>

              <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E5E5]">
                  <h3 className="font-display font-semibold text-[#0A0A0A]">Buchungshistorie ({bookings.length})</h3>
                </div>
                {bookings.length === 0 ? (
                  <div className="p-10 text-center text-[#525252]">Für dieses Fahrzeug liegen noch keine Buchungen vor.</div>
                ) : (
                  <div className="divide-y divide-[#E5E5E5]">
                    {bookings.map((b) => {
                      const s = STATUS[b.status] || STATUS.pending;
                      return (
                        <div key={b.id} className="p-5 flex items-center gap-4 hover:bg-[#F4F4F4]" data-testid={`veh-booking-${b.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <div className="min-w-0">
                                <div className="text-xs text-[#525252]">{b.user_name || b.user_email}</div>
                                <div className="font-semibold text-[#0A0A0A] truncate">{b.location_name}</div>
                              </div>
                              <Badge className={`${s.cls} border-0 shrink-0`}>{s.label}</Badge>
                            </div>
                            <div className="mt-1.5 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-[#525252]">
                              <span><Calendar size={11} className="inline mr-1" /> {formatDateDE(b.start_date)} → {formatDateDE(b.end_date)}</span>
                              <span>{b.days} Tag{b.days !== 1 && "e"}</span>
                              <span className="font-semibold text-[#0A0A0A]">{b.total.toFixed(2)}€</span>
                            </div>
                          </div>
                          <Link to={`/admin/buchungen/${b.id}`} className="shrink-0">
                            <Button size="sm" variant="outline" data-testid={`veh-view-booking-${b.id}`}>
                              <Eye size={14} className="mr-1" /> Öffnen
                            </Button>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>
        </>
      )}

      {tab === "daten" && <OwnershipTab vehicleId={id} />}
      {tab === "wartung" && <MaintenanceTab vehicleId={id} />}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-[#A3A3A3] mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[#525252]">{label}</div>
        <div className="text-[#0A0A0A] font-medium break-words">{value}</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#525252]">
        <Icon size={14} className="text-[#E11226]" /> {label}
      </div>
      <div className={`font-display font-bold text-2xl mt-2 ${accent ? "text-[#E11226]" : "text-[#0A0A0A]"}`}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><Label className="mb-1.5 block text-xs text-[#525252]">{label}</Label>{children}</div>);
}

const OWNERSHIP_TYPES = [
  { value: "gekauft", label: "Gekauft (bar)" },
  { value: "leasing", label: "Leasing" },
  { value: "finanzierung", label: "Finanzierung" },
];
const INSURANCE_INTERVALS = [
  { value: "monatlich", label: "Monatlich" },
  { value: "vierteljaehrlich", label: "Vierteljährlich" },
  { value: "halbjaehrlich", label: "Halbjährlich" },
  { value: "jaehrlich", label: "Jährlich" },
];

function OwnershipTab({ vehicleId }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docUrl, setDocUrl] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/admin/vehicles/${vehicleId}/ownership`);
    setForm(data);
  };
  useEffect(() => { load(); }, [vehicleId]);
  useEffect(() => () => { if (docUrl) URL.revokeObjectURL(docUrl); }, [docUrl]);

  if (!form) return <div className="text-[#525252]">Lädt...</div>;

  const f = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/vehicles/${vehicleId}/ownership`, form);
      setForm(data);
      toast.success("Fahrzeugdaten gespeichert");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/vehicles/${vehicleId}/ownership/document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fahrzeugschein hochgeladen");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const viewDoc = async () => {
    setDocLoading(true);
    try {
      const { data } = await api.get(`/admin/vehicles/${vehicleId}/ownership/document`, { responseType: "blob" });
      setDocUrl(URL.createObjectURL(data));
    } catch (e) { toast.error(apiError(e)); }
    finally { setDocLoading(false); }
  };

  const deleteDoc = async () => {
    if (!window.confirm("Fahrzeugschein wirklich entfernen?")) return;
    try {
      await api.delete(`/admin/vehicles/${vehicleId}/ownership/document`);
      toast.success("Entfernt");
      if (docUrl) { URL.revokeObjectURL(docUrl); setDocUrl(null); }
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const isImage = form.registration_doc_filename?.match(/\.(jpg|jpeg|png|webp)$/i);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="vehicle-ownership-tab">
      <section className="lg:col-span-7 space-y-6">
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6">
          <h3 className="font-display font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
            <IdCard size={16} /> Zulassung & Identifikation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Kennzeichen">
                <Input value={form.license_plate || ""} onChange={(e) => f("license_plate")(e.target.value)} placeholder="z.B. HB-RF 1234" data-testid="own-license-plate" />
              </Field>
            </div>
            <Field label="Kaufdatum">
              <Input type="date" value={form.purchase_date || ""} onChange={(e) => f("purchase_date")(e.target.value || null)} data-testid="own-purchase-date" />
            </Field>
            <Field label="Erstzulassung">
              <Input type="date" value={form.first_registration_date || ""} onChange={(e) => f("first_registration_date")(e.target.value || null)} data-testid="own-first-reg" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Fahrgestellnummer (VIN)">
                <Input value={form.vin || ""} onChange={(e) => f("vin")(e.target.value)} placeholder="z.B. WVWZZZ1KZAW123456" data-testid="own-vin" />
              </Field>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6">
          <h3 className="font-display font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
            <Euro size={16} /> Finanzierung
          </h3>
          <Field label="Art der Finanzierung">
            <Select value={form.ownership_type || ""} onValueChange={(v) => f("ownership_type")(v)}>
              <SelectTrigger data-testid="own-type"><SelectValue placeholder="Auswählen" /></SelectTrigger>
              <SelectContent>
                {OWNERSHIP_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {(form.ownership_type === "leasing" || form.ownership_type === "finanzierung") && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Monatliche Rate (€)">
                <Input type="number" step="0.01" value={form.monthly_rate ?? ""} onChange={(e) => f("monthly_rate")(e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="own-monthly-rate" />
              </Field>
              <Field label="Laufzeit (Monate)">
                <Input type="number" step="1" value={form.contract_duration_months ?? ""} onChange={(e) => f("contract_duration_months")(e.target.value === "" ? null : parseInt(e.target.value) || 0)} data-testid="own-duration" />
              </Field>
            </div>
          )}
          {form.ownership_type === "gekauft" && (
            <div className="mt-4">
              <Field label="Kaufpreis (€)">
                <Input type="number" step="0.01" value={form.purchase_price ?? ""} onChange={(e) => f("purchase_price")(e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="own-purchase-price" />
              </Field>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6" data-testid="own-insurance-card">
          <h3 className="font-display font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
            <ShieldCheck size={16} /> Versicherung
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Name der Versicherung">
                <Input value={form.insurance_company || ""} onChange={(e) => f("insurance_company")(e.target.value)} placeholder="z.B. Allianz" data-testid="own-insurer" />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Versicherungsnummer">
                <Input value={form.insurance_number || ""} onChange={(e) => f("insurance_number")(e.target.value)} placeholder="z.B. VN-123456789" data-testid="own-ins-number" />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Adresse">
                <Textarea value={form.insurance_address || ""} onChange={(e) => f("insurance_address")(e.target.value)} rows={2} placeholder="Straße, PLZ, Stadt" data-testid="own-ins-address" />
              </Field>
            </div>
            <Field label="Telefonnummer">
              <Input value={form.insurance_phone || ""} onChange={(e) => f("insurance_phone")(e.target.value)} placeholder="z.B. 0800 1234567" data-testid="own-ins-phone" />
            </Field>
            <Field label="Notfallnummer">
              <Input value={form.insurance_emergency_phone || ""} onChange={(e) => f("insurance_emergency_phone")(e.target.value)} placeholder="z.B. 0800 7654321" data-testid="own-ins-emergency" />
            </Field>
            <Field label="Zahlungsintervall">
              <Select value={form.insurance_interval || ""} onValueChange={(v) => f("insurance_interval")(v)}>
                <SelectTrigger data-testid="own-ins-interval"><SelectValue placeholder="Auswählen" /></SelectTrigger>
                <SelectContent>
                  {INSURANCE_INTERVALS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Kosten (€)">
              <Input type="number" step="0.01" value={form.insurance_cost ?? ""} onChange={(e) => f("insurance_cost")(e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="own-ins-cost" />
            </Field>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="own-save">
          <Save size={14} className="mr-2" /> {saving ? "Speichert..." : "Speichern"}
        </Button>
      </section>

      <aside className="lg:col-span-5">
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6" data-testid="vehicle-registration-doc-card">
          <h3 className="font-display font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
            <FileText size={16} /> Fahrzeugschein
          </h3>
          {form.registration_doc_filename ? (
            <>
              <div className="text-sm text-[#0A0A0A] font-medium truncate mb-3">{form.registration_doc_filename}</div>
              {!docUrl ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={viewDoc} disabled={docLoading} data-testid="own-doc-view">
                    <Eye size={14} className="mr-1.5" /> {docLoading ? "Lädt..." : "Ansehen"}
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={deleteDoc} data-testid="own-doc-delete">
                    <Trash2 size={14} />
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="border border-[#E5E5E5] rounded-md overflow-hidden bg-[#F4F4F4]">
                    {isImage ? (
                      <img src={docUrl} alt="Fahrzeugschein" className="w-full max-h-80 object-contain" />
                    ) : (
                      <iframe src={docUrl} title="Fahrzeugschein" className="w-full h-80" />
                    )}
                  </div>
                  <a href={docUrl} target="_blank" rel="noreferrer" className="text-xs text-[#E11226] hover:underline mt-2 inline-flex items-center gap-1">
                    <ExternalLink size={11} /> In neuem Tab öffnen
                  </a>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-[#525252] mb-3">Noch kein Fahrzeugschein hochgeladen.</p>
          )}
          <div className="mt-4 pt-4 border-t border-[#F4F4F4]">
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])} />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="own-doc-upload">
              <Upload size={14} className="mr-1.5" /> {uploading ? "Lädt hoch..." : "Datei hochladen"}
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}

const EMPTY_MAINT = {
  date: new Date().toISOString().slice(0, 10), cost: 0, description: "",
  workshop_name: "", workshop_address: "", workshop_phone: "",
};

function MaintenanceTab({ vehicleId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MAINT);
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/vehicles/${vehicleId}/maintenance`);
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [vehicleId]);

  const totalCost = useMemo(() => items.reduce((s, i) => s + (i.cost || 0), 0), [items]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY_MAINT }); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      date: item.date, cost: item.cost, description: item.description,
      workshop_name: item.workshop_name || "", workshop_address: item.workshop_address || "", workshop_phone: item.workshop_phone || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.date) { toast.error("Datum ist Pflichtfeld"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/vehicles/${vehicleId}/maintenance/${editing.id}`, form);
        toast.success("Eintrag aktualisiert");
      } else {
        await api.post(`/admin/vehicles/${vehicleId}/maintenance`, form);
        toast.success("Eintrag angelegt");
      }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (item) => {
    if (!window.confirm("Wartungseintrag wirklich löschen?")) return;
    try {
      await api.delete(`/admin/vehicles/${vehicleId}/maintenance/${item.id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadInvoice = async (item, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/vehicles/${vehicleId}/maintenance/${item.id}/invoice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Rechnung hochgeladen");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewInvoice = async (item) => {
    try {
      const { data } = await api.get(`/admin/vehicles/${vehicleId}/maintenance/${item.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="vehicle-maintenance-tab">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FEE2E5] text-[#E11226] flex items-center justify-center">
            <Wrench size={18} />
          </div>
          <div>
            <div className="font-display font-semibold text-[#0A0A0A]">Wartungshistorie</div>
            <div className="text-xs text-[#525252]">{items.length} Einträge · {totalCost.toFixed(2)}€ Gesamtkosten</div>
          </div>
        </div>
        <Button onClick={openNew} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="maint-new-btn">
          <Plus size={16} className="mr-2" /> Neuer Eintrag
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#525252]">Lädt...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#525252]">Noch keine Wartungseinträge vorhanden.</div>
        ) : (
          <div className="divide-y divide-[#E5E5E5]">
            {items.map((item) => (
              <div key={item.id} className="p-5 flex items-start gap-4" data-testid={`maint-item-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-semibold text-[#0A0A0A]">{formatDateDE(item.date)}</span>
                    <span className="font-display font-bold text-[#E11226]">{item.cost.toFixed(2)}€</span>
                  </div>
                  {item.description && <p className="text-sm text-[#525252] mt-1">{item.description}</p>}
                  {item.workshop_name && (
                    <div className="text-xs text-[#525252] mt-1.5 flex items-center gap-1.5">
                      <Wrench size={11} className="text-[#A3A3A3]" />
                      <span className="font-medium text-[#0A0A0A]">{item.workshop_name}</span>
                      {item.workshop_address && <span>· {item.workshop_address}</span>}
                      {item.workshop_phone && <span>· {item.workshop_phone}</span>}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {item.invoice_filename ? (
                      <Button size="sm" variant="outline" onClick={() => viewInvoice(item)} data-testid={`maint-invoice-view-${item.id}`}>
                        <Eye size={13} className="mr-1.5" /> {item.invoice_filename}
                      </Button>
                    ) : (
                      <>
                        <input
                          ref={(el) => { fileRefs.current[item.id] = el; }}
                          type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
                          onChange={(e) => uploadInvoice(item, e.target.files?.[0])}
                        />
                        <Button size="sm" variant="outline" onClick={() => fileRefs.current[item.id]?.click()} data-testid={`maint-invoice-upload-${item.id}`}>
                          <Upload size={13} className="mr-1.5" /> Rechnung hochladen
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`maint-edit-${item.id}`}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(item)} data-testid={`maint-delete-${item.id}`}><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Wartungseintrag bearbeiten" : "Neuer Wartungseintrag"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Datum">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="maint-date" />
              </Field>
              <Field label="Kosten (€)">
                <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} data-testid="maint-cost" />
              </Field>
            </div>
            <Field label="Was wurde gemacht?">
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="z.B. Ölwechsel, Bremsbeläge vorne" data-testid="maint-description" />
            </Field>
            <div className="pt-3 border-t border-[#F4F4F4]">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-3">Werkstatt</div>
              <div className="space-y-3">
                <Field label="Name">
                  <Input value={form.workshop_name} onChange={(e) => setForm({ ...form, workshop_name: e.target.value })} placeholder="z.B. KFZ-Werkstatt Meier" data-testid="maint-workshop-name" />
                </Field>
                <Field label="Adresse">
                  <Input value={form.workshop_address} onChange={(e) => setForm({ ...form, workshop_address: e.target.value })} placeholder="Straße, PLZ, Stadt" data-testid="maint-workshop-address" />
                </Field>
                <Field label="Telefonnummer">
                  <Input value={form.workshop_phone} onChange={(e) => setForm({ ...form, workshop_phone: e.target.value })} data-testid="maint-workshop-phone" />
                </Field>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="maint-save">
              {saving ? "Speichert..." : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
